import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { banner, search } from "../../imgUrl";
import { fetchOrders, fetchMediaData } from "../../features/orders/ordersSlice";
import { fetchAddressData } from "../../features/address/addressSlice";
import { getUserId } from "../../features/feature1/feature1Slice";
import moment from "moment";
import Invoice from "../Invoice";
import { ToastContainer, toast } from "react-toastify"; // Import Toastify
import Spinner from "react-bootstrap/Spinner"; // Import the Spinner component

const OrderTracking = () => {
  const [orderId, setOrderId] = useState("");
  const [searchedOrder, setSearchedOrder] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, status, mediaData, error } = useSelector(
    (state) => state.orders
  );
  const { addressData, addressStatus, addressError } = useSelector(
    (state) => state.address
  );
  const { userId, userStatus, userError } = useSelector((state) => state.user);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please log in first.");
      navigate("/login");
    } else {
      dispatch(fetchOrders()).then((result) => {
        dispatch(fetchMediaData(result.payload));
      });

      if (!userId) {
        dispatch(getUserId());
      }
    }
  }, [dispatch, navigate, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchAddressData(userId));
    }
  }, [dispatch, userId]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (orderId) {
      const foundOrder = orders.find((order) => order._id === orderId);
      setSearchedOrder(foundOrder || null);
      if (!foundOrder) {
        toast.error("Order not found!");      }
    } else {
      toast.warn("Please enter an order ID.");    }
  };

  if (
    status === "loading" ||
    addressStatus === "loading" ||
    userStatus === "loading"
  ) {
    return (
      <div className="text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (status === "failed") {
    return <div>Error: {error}</div>;
  }

  const order = searchedOrder;

  const statusMap = {
    pending: ["Order Received"],
    dispatch: [
      "Order Received",
      "Shipment Details Received",
      "Out for Delivery",
    ],
    delivered: [
      "Order Received",
      "Shipment Details Received",
      "Out for Delivery",
      "Delivered",
    ],
  };

  const isStepCompleted = (step) => {
    return order && statusMap[order.status]?.includes(step);
  };

  const getShippingAddress = () => {
    if (addressData && addressData.length > 0) {
      const address = addressData[0];
      return `${address.address}, ${address.city}, ${address.state}, ${address.pincode}`;
    }
    return "Address not available";
  };

  return (
    <div className="main_container py-5">
      {/* Search Input */}
      <div className="row justify-content-center">
        <div className="col-sm-6">
          <div className="my-5">
            <form onSubmit={handleSearch}>
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Enter Order ID"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                />
                <button type="submit" className="btn search-btn">
                  <img src={search} alt="Search Icon" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Order Tracking Section */}
      {order ? (
        <>
          <div className="container padding-bottom-3x mb-1">
            <div className="card mb-3">
              <div className="p-4 text-center text-white text-lg bg-dark rounded-top">
                <span className="text-uppercase">Tracking Order No - </span>
                <span className="text-medium">{order._id}</span>
              </div>

              <div className="card-body">
                <div className="steps d-flex flex-wrap flex-sm-nowrap justify-content-between padding-top-2x padding-bottom-1x">
                  {[
                    "Order Received",
                    "Shipment Details Received",
                    "Out for Delivery",
                    "Delivered",
                  ].map((step) => (
                    <div
                      key={step}
                      className={`step ${
                        isStepCompleted(step) ? "completed" : ""
                      }`}
                    >
                      <div className="step-icon-wrap">
                        <div className="step-icon">
                          <i
                            className={`pe-7s-${
                              step === "Order Received"
                                ? "cart"
                                : step === "Shipment Details Received"
                                ? "config"
                                : step === "Out for Delivery"
                                ? "car"
                                : "home"
                            }`}
                          ></i>
                        </div>
                      </div>
                      <h4 className="step-title">{step}</h4>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="container my-5">
            <div className="card">
              <div className="order-details p-3">
                <div className="d-flex justify-content-between">
                  <div className="d-flex gap-3">
                    <h5>Order Details</h5>
                    <p>
                      <strong>Order Date:</strong>{" "}
                      {moment(order.createdAt).format("DD MMMM YYYY")}
                    </p>
                  </div>
                  <div className="text-left text-sm-right">
                    <Invoice orderId={orderId} />
                  </div>
                </div>
              </div>

              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Shipping Address</th>
                    <th scope="col">Payment Method</th>
                    <th scope="col">Order Summary</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">1</th>
                    <td>{getShippingAddress()}</td>
                    <td>{order.order_type || "N/A"}</td>
                    <td>
                      <table className="table">
                        <tbody>
                          <tr>
                            <th scope="row">Subtotal</th>
                            <td>₹{order.subTotal}</td>
                          </tr>
                          <tr>
                            <th scope="row">Shipping Cost</th>
                            <td>₹{order.shippingCost}</td>
                          </tr>
                          <tr>
                            <th scope="row">Total</th>
                            <td>₹{order.total}</td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="product-details mt-4 p-3">
                <h5>Product Details</h5>
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Image</th>
                      <th scope="col">Product</th>
                      <th scope="col">Price</th>
                      <th scope="col">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.products?.map((item, index) => (
                      <tr key={item._id}>
                        <th scope="row">{index + 1}</th>
                        <td>
                          {mediaData[item.media_id] ? (
                            <img
                              src={mediaData[item.media_id].url}
                              style={{ width: "50px", height: "50px" }}
                              alt={item.title}
                            />
                          ) : (
                            <p>Loading image...</p>
                          )}
                        </td>
                        <td>{item.product.title}</td>
                        <td>₹{item.price}</td>
                        <td>{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center mt-5">
          <h3>No order found. Please search for an order.</h3>
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
